import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { mapGetters } from "vuex";
import Context from "@/mixins/context";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import { IAccount } from "@/models/accounts";
import { STATS_ALL_CURRENCY_CODE } from "@/data/constants";

@Component({
  computed: {
    ...mapGetters({
      brandCurrencies: "brand/currencies"
    })
  }
})
export default class FetchStatsMixin extends Mixins(
  RequestPromises,
  Context,
  CurrenciesStore
) {
  @Prop({ required: true })
  public id!: string;

  @Prop({ default: null })
  public accountId!: IAccount["id"];

  @Prop({ default: null })
  public currencyCode!: string;

  @Prop({ default: true })
  public currencyBound!: string;

  @Prop({ required: true })
  public valuePath!: string;

  @Prop({ required: false, default: null })
  public valueFunc!: (arg: any) => any;

  @Prop({ required: true })
  public params!: any;

  @Prop({ required: true })
  public scope!: string;

  @Prop({ default: null })
  public defaultValue!: any;

  @Prop({ default: DataModules.STATS })
  public dataModule!: any;

  @Prop({ default: "get" })
  public dataModuleAction!: any;

  private debounceLoadData = this.$_.debounce(() => {
    this.loadData();
  }, 500);

  public brandCurrencies!: any[];
  public isLoading = true;
  public showOptions = false;
  public callCount = 0;

  public statResult: any = null;

  public async created() {
    if (this.currencyBound && !this.currencyCode) return;
    if (this.contentAvailable) {
      this.loadData();
    }
  }

  public beforeDestroy() {
    if (this.contentAvailable) {
      this.$store.dispatch("data/bin", {
        storeModule: this.dataModule,
        scope: this.innerScope,
        vm: this
      });
    }
  }

  // Watch
  @Watch("params")
  public onParamsChagned(newVal, oldval) {
    if (!this.$_.isEqual(newVal, oldval)) this.debounceLoadData();
  }

  @Watch("currencyCode")
  public onCurrencyCodeChagned(newVal, oldval) {
    if (!this.$_.isEqual(newVal, oldval)) this.debounceLoadData();
  }

  // Computed
  public get contentAvailable() {
    return this.$userCan("list_statistics");
  }

  public get availableCurrencies() {
    // If brand has more than 1 currency include all in the available
    const currencies = this.$_.map(this.brandCurrencies, i => i.code);
    return currencies.length > 1
      ? [STATS_ALL_CURRENCY_CODE, ...currencies]
      : currencies;
  }

  public get brandCurrencyId() {
    return this.$_.get(
      this.$store,
      "state.brand.currency_id",
      STATS_ALL_CURRENCY_CODE
    );
  }

  public async loadData(ignoreStored = false) {
    if (!this.contentAvailable) return;
    try {
      this.isLoading = true;
      await this.$store.dispatch("constants/getCurrencies");

      await this.$store.dispatch(
        `data/${this.dataModule}/${this.dataModuleAction}`,
        {
          ignoreStored,
          id: this.id,
          scope: this.innerScope,
          vm: this,
          params: this.accountId
            ? {
                ...this.params,
                account_id: this.accountId,
                currency_code: this.currencyCode
              }
            : { ...this.params, currency_code: this.currencyCode }
        }
      );

      this.statResult = await this.getStatValue(this.currencyCode);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.callCount++;
      this.isLoading = false;
    }
  }

  public isPromise(value) {
    return this.$_.has(value, "then") && this.$_.isFunction(value.then);
  }

  public async recalculateStatValue() {
    this.statResult = await this.getStatValue(this.currencyCode);
  }

  public getStatValue(currencyCode) {
    const path = this.valuePath.replace("{currencyCode}", currencyCode);
    const value = this.$_.isEmpty(path)
      ? this.statObj
      : this.$_.get(this.statObj, path);

    // Get selected currency
    let currency = this.currencies.find(c => c.code === currencyCode);
    if (!currency)
      currency = this.$_.find(
        this.currencies,
        c => c.id === this.brandCurrencyId
      );

    return this.$_.isFunction(this.valueFunc) || this.isPromise(this.valueFunc)
      ? this.valueFunc({
          defaultValue: this.defaultValue,
          currencyBound: this.currencyBound,
          currency,
          currencyCode: currencyCode || currency?.code,
          data: value,
          params: this.params,
          isLoading: this.isLoading
        })
      : this.$_.isNil(value)
      ? this.defaultValue
      : value;
  }

  public hideOptions() {
    this.showOptions = false;
  }

  public get innerScope() {
    return this.accountId ? `${this.scope}${this.accountId}` : this.scope;
  }

  public get statObj() {
    return this.$store.getters["data/single"]({
      storeModule: this.dataModule,
      scope: this.innerScope,
      id: this.id
    });
  }
}
